/**
 * Custom Domain API Client
 *
 * Thin wrapper around fetch for Builder Service API calls.
 * All requests require Firebase Auth token.
 *
 * IMPORTANT: Uses Load Balancer URL (VITE_BUILDER_API_URL),
 * not direct Cloud Run URLs which return 404.
 *
 * Transforms API responses to frontend CustomDomain type.
 */

import { getAuth } from 'firebase/auth';
import { getBuilderApiUrl } from '@modules/custom-domains/services/articlePathModeEnv';
import type {
  CustomDomain,
  CustomDomainStatus,
  CustomDomainSurface,
  DomainSetupResponse,
  DomainVerifyResponse,
  DomainStatusResponse,
  CustomDomainError,
  DnsRecords,
  ArticleBaseUrlVerifyResult,
  ArticleModeSwitchJob,
  ArticlePathModeClearResult,
  ArticlePathModeState,
  ArticleSiteMode,
} from '@modules/custom-domains/types/customDomain.types';

/**
 * Raw API response from /api/custom-domain/status
 * Different structure from frontend CustomDomain type
 */
interface StatusApiResponse {
  hasDomain: boolean;
  domainId?: string;
  domain?: string;
  status?: CustomDomainStatus;
  /** New format: both DNS records */
  dnsRecords?: {
    aRecord: {
      host: string;
      type: 'A';
      value: string;
    };
    cnameRecord: {
      host: string;
      type: 'CNAME';
      target: string;
    };
  };
  /** Legacy format */
  dnsRecord?: {
    name: string;
    type: 'CNAME';
    value: string;
  };
  certificate?: {
    state: string;
    expireTime?: string;
  };
  lastError?: CustomDomainError;
  createdAt?: string;
  activatedAt?: string;
  /** Article surface only (same reader as /article-site, AC-044) */
  mode?: ArticleSiteMode;
  publicBaseUrl?: string | null;
  homeAlias?: string | null;
  modeSwitchJob?: ArticleModeSwitchJob | null;
}

/**
 * Raw API response from /api/custom-domain/setup
 */
interface SetupApiResponse {
  domainId: string;
  domain: string;
  status: CustomDomainStatus;
  /** New format: both DNS records */
  dnsRecords?: {
    aRecord: {
      host: string;
      type: 'A';
      value: string;
    };
    cnameRecord: {
      host: string;
      type: 'CNAME';
      target: string;
    };
  };
  /** Legacy format */
  dnsRecord: {
    name: string;
    type: 'CNAME';
    value: string;
  };
  message: string;
}

/**
 * API error with code for programmatic handling
 */
export class CustomDomainApiError extends Error {
  code: string;
  status: number;
  /**
   * Extra fields of the problem body (path mode): `jobId` of a 409 mode_switch_in_progress,
   * `retryAfterSeconds` of a 429 rate_limited, `field` / `reason` of an invalid_base_url.
   */
  details?: Record<string, unknown>;

  constructor(message: string, code: string, status: number, details?: Record<string, unknown>) {
    super(message);
    this.name = 'CustomDomainApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

/** Problem-body fields beyond the standard four (jobId, retryAfterSeconds, field, reason); undefined when none. */
function problemDetails(body: Record<string, unknown>): Record<string, unknown> | undefined {
  const details = { ...body };
  for (const key of ['success', 'code', 'message', 'error']) delete details[key];
  return Object.keys(details).length > 0 ? details : undefined;
}

/**
 * Get Firebase Auth token for current user
 */
async function getAuthToken(): Promise<string> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new CustomDomainApiError(
      'Not authenticated',
      'UNAUTHORIZED',
      401
    );
  }
  return user.getIdToken();
}

/**
 * Generic API request with auth
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAuthToken();
  const baseUrl = getBuilderApiUrl();

  const response = await fetch(`${baseUrl}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });

  // Parse response body
  const body = await response.json().catch(() => ({}));

  // Handle errors
  if (!response.ok) {
    throw new CustomDomainApiError(
      body.message || body.error || `API error: ${response.status}`,
      body.code || 'UNKNOWN_ERROR',
      response.status,
      problemDetails(body)
    );
  }

  return body as T;
}

/**
 * Transform API status response to CustomDomain type
 */
function transformStatusResponse(response: StatusApiResponse): CustomDomain | null {
  if (!response.hasDomain || !response.domain) {
    return null;
  }

  // Build dnsRecords from new format if available
  const dnsRecords: DnsRecords | undefined = response.dnsRecords
    ? {
        aRecord: {
          host: response.dnsRecords.aRecord.host,
          type: 'A',
          value: response.dnsRecords.aRecord.value,
        },
        cnameRecord: {
          host: response.dnsRecords.cnameRecord.host,
          type: 'CNAME',
          target: response.dnsRecords.cnameRecord.target,
        },
      }
    : undefined;

  return {
    id: response.domainId || '',
    domain: response.domain,
    projectId: '', // Not returned by API, not needed for display
    status: response.status || 'pending_dns',
    dnsRecords,
    dnsAuthorization: response.dnsRecord
      ? {
          name: '', // Not returned by status API
          cnameRecord: {
            name: response.dnsRecord.name,
            type: 'CNAME',
            data: response.dnsRecord.value, // API returns "value", type expects "data"
          },
          state: 'AUTHORIZING',
          createdAt: response.createdAt || '',
        }
      : null,
    certificate: response.certificate
      ? {
          name: '',
          state: response.certificate.state as 'PROVISIONING' | 'ACTIVE' | 'FAILED' | 'PENDING_RENEWAL',
          expiresAt: response.certificate.expireTime,
          createdAt: response.createdAt || '',
        }
      : null,
    certificateMapEntry: null,
    createdAt: response.createdAt || '',
    updatedAt: response.createdAt || '',
    activatedAt: response.activatedAt,
    createdBy: '',
    lastError: response.lastError,
    // Article surface only: path mode state rides along on the status read (AC-044).
    ...(response.mode !== undefined ? { mode: response.mode } : {}),
    ...(response.publicBaseUrl !== undefined ? { publicBaseUrl: response.publicBaseUrl } : {}),
    ...(response.homeAlias !== undefined ? { homeAlias: response.homeAlias } : {}),
    ...(response.modeSwitchJob !== undefined ? { modeSwitchJob: response.modeSwitchJob } : {}),
  };
}

/**
 * Transform API setup response to DomainSetupResponse type
 */
function transformSetupResponse(response: SetupApiResponse): DomainSetupResponse {
  // Build dnsRecords from new format or construct from legacy
  const dnsRecords: DnsRecords = response.dnsRecords
    ? {
        aRecord: {
          host: response.dnsRecords.aRecord.host,
          type: 'A',
          value: response.dnsRecords.aRecord.value,
        },
        cnameRecord: {
          host: response.dnsRecords.cnameRecord.host,
          type: 'CNAME',
          target: response.dnsRecords.cnameRecord.target,
        },
      }
    : {
        // Fallback: construct from legacy dnsRecord (A record IP unknown)
        aRecord: {
          host: response.domain,
          type: 'A',
          value: '(see documentation)', // Placeholder for legacy API
        },
        cnameRecord: {
          host: response.dnsRecord.name,
          type: 'CNAME',
          target: response.dnsRecord.value,
        },
      };

  return {
    success: true,
    domainId: response.domainId,
    status: response.status,
    dnsRecords,
    cnameRecord: {
      host: response.dnsRecord.name,
      target: response.dnsRecord.value,
    },
    instructions: response.message,
  };
}

/** Path-mode endpoints answer `{ success: true, data }`; the data is what the callers need. */
async function pathModeRequest<T>(endpoint: string, body?: Record<string, unknown>): Promise<T> {
  const envelope = await apiRequest<{ success: boolean; data: T }>(
    endpoint,
    body === undefined ? undefined : { method: 'POST', body: JSON.stringify(body) }
  );
  return envelope.data;
}

/**
 * Custom Domain API client
 */
export const customDomainApi = {
  /**
   * Get custom domain status for a project
   * Returns null if no domain configured
   */
  async getStatus(
    projectId: string,
    surface: CustomDomainSurface = 'web'
  ): Promise<CustomDomain | null> {
    try {
      const response = await apiRequest<StatusApiResponse>(
        `/api/custom-domain/status?projectId=${encodeURIComponent(projectId)}&surface=${surface}`
      );
      return transformStatusResponse(response);
    } catch (error) {
      // 404 means no domain configured - return null
      if (error instanceof CustomDomainApiError && error.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Setup a new custom domain
   * Returns domain setup response with CNAME instructions
   */
  async setup(
    projectId: string,
    domain: string,
    surface: CustomDomainSurface = 'web'
  ): Promise<DomainSetupResponse> {
    const response = await apiRequest<SetupApiResponse>('/api/custom-domain/setup', {
      method: 'POST',
      body: JSON.stringify({ projectId, domain, surface }),
    });
    return transformSetupResponse(response);
  },

  /**
   * Trigger manual DNS verification
   */
  async verify(
    projectId: string,
    surface: CustomDomainSurface = 'web'
  ): Promise<DomainVerifyResponse> {
    return apiRequest<DomainVerifyResponse>('/api/custom-domain/verify', {
      method: 'POST',
      body: JSON.stringify({ projectId, surface }),
    });
  },

  /**
   * Remove custom domain from project
   */
  async remove(projectId: string, surface: CustomDomainSurface = 'web'): Promise<void> {
    await apiRequest<{ success: boolean }>('/api/custom-domain/remove', {
      method: 'DELETE',
      body: JSON.stringify({ projectId, surface }),
    });
  },

  /**
   * Get detailed status with progress info
   */
  async getDetailedStatus(
    projectId: string,
    surface: CustomDomainSurface = 'web'
  ): Promise<DomainStatusResponse | null> {
    try {
      return await apiRequest<DomainStatusResponse>(
        `/api/custom-domain/status?projectId=${encodeURIComponent(projectId)}&surface=${surface}&detailed=true`
      );
    } catch (error) {
      if (error instanceof CustomDomainApiError && error.status === 404) {
        return null;
      }
      throw error;
    }
  },

  // ---------------------------------------------------------------------------
  // Article path mode (articles.access). The server owns the state; these only transport it.
  // ---------------------------------------------------------------------------

  /** Article domain + path-mode state, including the mode switch job to resume. */
  async getArticleSite(projectId: string): Promise<ArticlePathModeState> {
    return pathModeRequest<ArticlePathModeState>(`/api/custom-domain/article-site?projectId=${encodeURIComponent(projectId)}`);
  },

  /** Read-only check of a base URL (nothing stored). The UI's "Verify & enable" uses `setBaseUrl` instead (decision #10). */
  async verifyBaseUrl(projectId: string, publicBaseUrl: string): Promise<ArticleBaseUrlVerifyResult> {
    return pathModeRequest<ArticleBaseUrlVerifyResult>('/api/custom-domain/base-url/verify', { projectId, publicBaseUrl });
  },

  /** Verify + store + first step of the republish job. Answers the state with the running job. */
  async setBaseUrl(projectId: string, publicBaseUrl: string): Promise<ArticlePathModeState> {
    return pathModeRequest<ArticlePathModeState>('/api/custom-domain/base-url/set', { projectId, publicBaseUrl });
  },

  /** Back to subdomain mode (starts the republish job; `note` when there was nothing to clear). */
  async clearBaseUrl(projectId: string): Promise<ArticlePathModeClearResult> {
    return pathModeRequest<ArticlePathModeClearResult>('/api/custom-domain/base-url/clear', { projectId });
  },

  /** Advances a running job by one bounded batch; any client may step it. */
  async stepBaseUrl(projectId: string, jobId: string): Promise<ArticlePathModeState> {
    return pathModeRequest<ArticlePathModeState>('/api/custom-domain/base-url/step', { projectId, jobId });
  },

  /** The live article served at the site root. */
  async setHome(projectId: string, alias: string): Promise<ArticlePathModeState> {
    return pathModeRequest<ArticlePathModeState>('/api/custom-domain/home/set', { projectId, alias });
  },

  async clearHome(projectId: string): Promise<ArticlePathModeState> {
    return pathModeRequest<ArticlePathModeState>('/api/custom-domain/home/clear', { projectId });
  },
};

export default customDomainApi;
